import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import {
  CoverageAreaDriverEntry,
  CoverageAreaProps,
} from "store/modules/Logistics/types.store";
import { getCoverageAreaDealerId } from "store/modules/Logistics/logisticsCoverageArea.helpers";

export const MAX_DRIVERS_PER_MAP_COVERAGE_AREA = 3;

export type DriverOption = { id: string; label: string };

function parseDriverEntryFromApi(
  raw: unknown,
  dealerId: string,
  fallbackIndex: number,
): CoverageAreaDriverEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const details = r.details as Record<string, unknown> | undefined;
  const driver = r.driver as Record<string, unknown> | undefined;
  if (driver && typeof driver.id === "string") {
    return {
      priority: typeof r.priority === "number" ? r.priority : fallbackIndex,
      details: {
        id: driver.id,
        dealerId,
        name: typeof driver.name === "string" ? driver.name : undefined,
        status: typeof driver.status === "string" ? driver.status : undefined,
      },
    };
  }
  if (details && typeof details.id === "string") {
    return {
      id: typeof r.id === "string" ? r.id : undefined,
      priority: typeof r.priority === "number" ? r.priority : fallbackIndex,
      details: {
        id: details.id,
        dealerId:
          typeof details.dealerId === "string" ? details.dealerId : dealerId,
        name: typeof details.name === "string" ? details.name : undefined,
        surname:
          typeof details.surname === "string" ? details.surname : undefined,
        enabled: details.enabled !== false,
      },
    };
  }
  if (typeof r.driverId === "string") {
    return {
      priority: typeof r.priority === "number" ? r.priority : fallbackIndex,
      details: {
        id: r.driverId,
        dealerId,
        enabled: true,
      },
    };
  }
  if (typeof r.id === "string") {
    return {
      priority: typeof r.priority === "number" ? r.priority : fallbackIndex,
      details: {
        id: r.id,
        dealerId,
        name: typeof r.name === "string" ? r.name : undefined,
        surname: typeof r.surname === "string" ? r.surname : undefined,
        enabled: true,
      },
    };
  }
  return null;
}

export function enrichCoverageAreaDriversFromApi(
  coverageArea: CoverageAreaProps,
): Pick<CoverageAreaProps, "drivers" | "hasDriver"> {
  const dealerId = getCoverageAreaDealerId(coverageArea);
  const { drivers: driversRaw } = coverageArea;
  let drivers: CoverageAreaDriverEntry[] = [];

  if (Array.isArray(driversRaw) && driversRaw.length > 0) {
    drivers = driversRaw
      .map((d, i) => parseDriverEntryFromApi(d, dealerId, i))
      .filter((x): x is CoverageAreaDriverEntry => x !== null)
      .sort((a, b) => a.priority - b.priority);
  }

  return {
    drivers,
    hasDriver: drivers.length > 0,
  };
}

export function getOrderedCoverageAreaDrivers(
  area: CoverageAreaProps,
): CoverageAreaDriverEntry[] {
  const fromState = area.drivers ?? [];
  if (fromState.length === 0) {
    return [];
  }

  return [...fromState].sort((a, b) => a.priority - b.priority);
}

export function dealerUsersToDriverOptions(
  users: DealerUsersProps[],
): DriverOption[] {
  return users.map((u) => ({
    id: u.id,
    label: `${u.name ?? ""} ${u.surname ?? ""}`.trim(),
  }));
}

export function resolveDriverDisplayName(
  entry: CoverageAreaDriverEntry,
  users: DealerUsersProps[],
): string {
  const user = users.find((u) => u.id === entry.details.id);
  if (user) {
    return `${user.name ?? ""} ${user.surname ?? ""}`.trim();
  }

  return [entry.details.name, entry.details.surname]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function driverOptionFromEntry(
  entry: CoverageAreaDriverEntry,
  users: DealerUsersProps[],
): DriverOption {
  return {
    id: entry.details.id,
    label: resolveDriverDisplayName(entry, users),
  };
}
